import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getIconClass, sortServices } from '../lib/utils';

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all'); // 'all', 'porte', 'sikring'

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/services')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setServices(data);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching services:', err);
        setLoading(false);
      });
  }, []);

  const isPortService = (service) => !service.service_slug?.startsWith('alarmsystem');
  const isSecurityService = (service) => service.service_slug?.startsWith('alarmsystem');

  const filteredServices = services.filter((service) => {
    if (activeCategory === 'porte') return isPortService(service);
    if (activeCategory === 'sikring') return isSecurityService(service);
    return true;
  });

  return (
    <div className="bg-brand-bg">
      <SEO
        title="Vi Tilbyder: Portservice, Porte & Ajax Sikring"
        description="Fuld oversigt over vores ydelser: Lovpligtigt portservice og eftersyn, garageporte, friporte, industriporte samt intelligente Ajax alarmsystemer og videoovervågning."
        url="/ydelser"
        keywords="portservice, lovpligtigt eftersyn, garageporte, industriporte, hurtigporte, friporte, Ajax alarmsystem, videoovervågning, Pabst Teknik"
      />
      {/* Page Header */}
      <section className="relative bg-gradient-to-br from-slate-950 via-brand-dark to-brand-accent/20 text-white px-6 py-24 md:py-32 overflow-hidden">
        {/* Background Image with Blend Mode */}
        <div className="absolute inset-0 opacity-15 mix-blend-overlay">
          <img
            src="https://assets.auxodev.dk/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/webp-portservise-cover-2-85-11zon.webp"
            alt="Pabst Teknik ApS ydelser inden for portservice, industriporte og sikring"
            className="h-full w-full object-cover"
          />
        </div>
        
        {/* Subtle Tech Grid Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(2,132,199,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(2,132,199,0.08)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]" />
        
        {/* Glowing Ambient Light */}
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-accent/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-accent/10 rounded-full blur-3xl pointer-events-none" />

        <div className="mx-auto max-w-7xl relative z-10 flex flex-col gap-6 items-center text-center">
          {/* Breadcrumb Navigation */}
          <nav aria-label="Brødkrummesti" className="flex items-center gap-2 text-xs sm:text-sm font-medium text-slate-300">
            <Link to="/" className="hover:text-white transition-colors flex items-center gap-1.5">
              <i className="fa-solid fa-house text-xs text-brand-accent" />
              <span>Forside</span>
            </Link>
            <i className="fa-solid fa-chevron-right text-[10px] text-slate-400" />
            <span className="text-brand-accent font-semibold" aria-current="page">Ydelser</span>
          </nav>

          {/* Floating Badge */}
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold bg-brand-accent/20 text-brand-accent-light border border-brand-accent/30 uppercase tracking-widest">
            <span className="h-2 w-2 rounded-full bg-brand-accent animate-pulse" />
            Din lokale port- og sikringspartner i Vejen
          </span>

          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-6xl font-display">
            Hvad kan vi <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-100 to-brand-accent">hjælpe med?</span>
          </h1>
          
          <p className="text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed font-sans">
            Hos <Link to="/" className="text-white hover:text-brand-accent underline decoration-brand-accent/50 underline-offset-2 transition-colors font-medium">Pabst Teknik</Link> er vi specialister i professionel portservice, lovpligtigt eftersyn samt salg og montage af alle typer porte – fra private garageporte og friporte til industriporte. Vi leverer også markedets mest moderne, intelligente Ajax sikrings- og alarmsystemer.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="relative z-20 px-6 pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl -mt-12 bg-white rounded-3xl border border-slate-100 p-8 md:p-14 shadow-xl">
          
          {/* Category Filter Tabs */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-12">
            <button
              onClick={() => setActiveCategory('all')}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-xs sm:text-sm font-bold transition-all ${
                activeCategory === 'all'
                  ? 'bg-brand-dark text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <i className="fa-regular fa-grid-2" />
              <span>Alt vi tilbyder ({services.length})</span>
            </button>

            <button
              onClick={() => setActiveCategory('porte')}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-xs sm:text-sm font-bold transition-all ${
                activeCategory === 'porte'
                  ? 'bg-brand-accent text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <i className="fa-regular fa-garage" />
              <span>Portservice & Porte ({services.filter(isPortService).length})</span>
            </button>

            <button
              onClick={() => setActiveCategory('sikring')}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-xs sm:text-sm font-bold transition-all ${
                activeCategory === 'sikring'
                  ? 'bg-emerald-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <i className="fa-regular fa-shield-halved" />
              <span>Sikring & Alarmer ({services.filter(isSecurityService).length})</span>
              <span className="rounded-full bg-emerald-100 px-1.5 py-0.2 text-[10px] text-emerald-800 font-bold uppercase">Nyhed</span>
            </button>
          </div>

          {/* Context Banner */}
          {activeCategory === 'porte' && (
            <div className="mb-10 rounded-2xl bg-sky-50/70 border border-sky-100 p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-accent text-white shadow-sm">
                  <i className="fa-solid fa-garage text-xl" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-display font-bold text-brand-dark text-base">Porte er vores kernekompetence</h3>
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 bg-white/80 border border-slate-200/80 px-2 py-0.5 rounded-full">
                      Salgspartner for Lindab portservice
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mt-0.5">
                    Vi leverer, monterer og servicerer alle porttyper – garageporte, friporte, industriporte og hurtigporte. Lindab fungerer som salgspartner for vores portservice for at sikre robuste løsninger.
                  </p>
                </div>
              </div>
              <a
                href="tel:+4521684422"
                className="inline-flex items-center gap-2 rounded-full bg-brand-dark px-5 py-2.5 text-xs font-bold text-white shadow hover:bg-slate-800 whitespace-nowrap"
              >
                <i className="fa-regular fa-phone text-brand-accent" />
                <span>Akut portservice: 21 68 44 22</span>
              </a>
            </div>
          )}

          {activeCategory === 'sikring' && (
            <div className="mb-10 rounded-2xl bg-emerald-50/70 border border-emerald-100 p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm">
                  <i className="fa-solid fa-shield-check text-xl" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-brand-dark text-base">Nyhed: Intelligente Sikringsløsninger fra Ajax</h3>
                  <p className="text-sm text-slate-600 mt-0.5">
                    Som autoriseret installatør tilbyder vi trådløse alarmsystemer, HD-videoovervågning, branddetektion og vandskadeforebyggelse til både bolig og erhverv.
                  </p>
                </div>
              </div>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-xs font-bold text-white shadow hover:bg-emerald-700 whitespace-nowrap"
              >
                <span>Få et sikringstilbud</span>
                <i className="fa-regular fa-arrow-right" />
              </Link>
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-accent" />
              <p className="text-sm font-semibold text-slate-500">Henter ydelser...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {sortServices(filteredServices).map((service) => {
                const isSecurity = isSecurityService(service);
                return (
                  <div
                    key={service.service_slug}
                    className="group flex flex-col rounded-2xl border border-slate-100 bg-slate-50 overflow-hidden transition-all hover:border-slate-200 hover:bg-white hover:shadow-xl"
                  >
                    {/* Image */}
                    <div className="relative h-56 overflow-hidden bg-slate-100">
                      <img
                        src={service.image && service.image[0] ? service.image[0] : 'https://assets.auxodev.dk/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/webp-starterkit-cam-plus-89-11zon.webp'}
                        alt={`${service.title.da} - Professionel portservice og sikring fra Pabst Teknik ApS`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      
                      {/* Icon */}
                      <div className={`absolute top-4 left-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-md transition-all ${
                        isSecurity 
                          ? 'text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white' 
                          : 'text-brand-accent group-hover:bg-brand-accent group-hover:text-white'
                      }`}>
                        <i className={`${getIconClass(service.icon)} text-xl`} />
                      </div>

                      {/* Category Tag */}
                      <div className="absolute top-4 right-4">
                        {isSecurity ? (
                          <span className="rounded-full bg-emerald-600/90 backdrop-blur-xs px-3 py-1 text-[10px] font-bold text-white uppercase tracking-wider shadow">
                            Sikring
                          </span>
                        ) : (
                          <span className="rounded-full bg-brand-dark/80 backdrop-blur-xs px-3 py-1 text-[10px] font-bold text-white uppercase tracking-wider shadow">
                            Porte & Service
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex flex-col p-6 sm:p-8 flex-grow">
                      <h3 className="font-display text-xl font-bold text-brand-dark mb-3 group-hover:text-brand-accent transition-colors">
                        {service.title.da}
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed mb-6 flex-grow">
                        {service.summary.da}
                      </p>
                      <Link
                        to={`/ydelser/${service.service_slug}`}
                        className={`inline-flex items-center gap-2 text-sm font-bold mt-auto transition-colors ${
                          isSecurity 
                            ? 'text-emerald-600 hover:text-emerald-700' 
                            : 'text-brand-accent hover:text-brand-accent-hover'
                        }`}
                      >
                        <span>Læs mere om ydelsen</span>
                        <i className="fa-regular fa-arrow-right text-xs transition-transform group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Cross-linking navigation row */}
          <div className="mt-16 pt-10 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-accent/10 text-brand-accent">
                  <i className="fa-regular fa-images text-xl" />
                </div>
                <div>
                  <h4 className="font-display font-bold text-brand-dark text-base">Se eksempler på vores arbejde</h4>
                  <p className="text-xs text-slate-500 mt-1">Udforsk vores billedgalleri med færdige portmonteringer og sikringsprojekter.</p>
                </div>
              </div>
              <Link
                to="/galleri"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-dark px-4 py-2.5 text-xs font-bold text-white hover:bg-slate-800 transition-colors shrink-0 shadow-xs"
              >
                <span>Gå til galleri</span>
                <i className="fa-regular fa-arrow-right text-[10px]" />
              </Link>
            </div>

            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-accent/10 text-brand-accent">
                  <i className="fa-regular fa-building-user text-xl" />
                </div>
                <div>
                  <h4 className="font-display font-bold text-brand-dark text-base">Hvem er Pabst Teknik?</h4>
                  <p className="text-xs text-slate-500 mt-1">Læs om vores baggrund, faste montører og officielle Lindab salgspartnerskab.</p>
                </div>
              </div>
              <Link
                to="/om-os"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-dark px-4 py-2.5 text-xs font-bold text-white hover:bg-slate-800 transition-colors shrink-0 shadow-xs"
              >
                <span>Læs om os</span>
                <i className="fa-regular fa-arrow-right text-[10px]" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-slate-50 border-t border-slate-100 px-6 py-20 md:py-24 text-center">
        <div className="mx-auto max-w-4xl flex flex-col items-center gap-8">
          <h2 className="text-3xl font-extrabold text-brand-dark sm:text-4xl">Har du en speciel opgave?</h2>
          <p className="text-lg text-slate-600 max-w-2xl leading-relaxed">
            Vi skræddersyr også specialløsninger, der kombinerer flere af vores ydelser. Kontakt os for en uforpligtende snak og professionel rådgivning.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <a
              href="tel:+4521684422"
              className="flex items-center justify-center gap-3 rounded-full bg-brand-dark px-8 py-4 text-base font-bold text-white shadow-lg transition-all hover:bg-slate-800"
            >
              <i className="fa-regular fa-phone text-brand-accent text-lg" />
              <span>Ring nu: 21 68 44 22</span>
            </a>
            <Link
              to="/kontakt"
              className="flex items-center justify-center gap-2 rounded-full bg-brand-accent px-8 py-4 text-base font-bold text-white shadow-lg transition-all hover:bg-brand-accent-hover"
            >
              <span>Få et uforpligtende tilbud</span>
              <i className="fa-regular fa-arrow-right" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
